// form_validation_controller.js
// Client-side form validation for Stimulus
//
// Usage:
//   <form data-controller="form-validation"
//         data-action="submit->form-validation#validate">
//     <input type="text"
//            required
//            data-form-validation-target="field"
//            data-form-validation-message="This field is required">
//   </form>

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "submit"]

  connect() {
    // Add validation on blur for immediate feedback
    this.fieldTargets.forEach(field => {
      field.addEventListener('blur', () => this.validateField(field))
      field.addEventListener('input', () => this.clearFieldError(field))
    })

    // Check if form is valid on load
    this.updateSubmitButton()
  }

  validate(event) {
    let isValid = true

    // Validate all fields
    this.fieldTargets.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false
      }
    })

    // Prevent submission if invalid
    if (!isValid) {
      event.preventDefault()
      event.stopPropagation()

      // Focus first invalid field
      const firstInvalid = this.element.querySelector('.is-invalid')
      if (firstInvalid) {
        firstInvalid.focus()
      }
    }

    return isValid
  }

  validateField(field) {
    const errors = []

    // Required validation
    if (field.hasAttribute('required') && !field.value.trim()) {
      errors.push(field.dataset.formValidationMessage || 'This field is required')
    }

    // Pattern validation (HTML5 pattern attribute)
    if (field.pattern && field.value) {
      const regex = new RegExp(field.pattern)
      if (!regex.test(field.value)) {
        errors.push(field.dataset.formValidationPatternMessage || 'Invalid format')
      }
    }

    // Min length validation
    if (field.minLength && field.value.length < field.minLength) {
      errors.push(`Minimum length is ${field.minLength} characters`)
    }

    // Max length validation
    if (field.maxLength && field.value.length > field.maxLength) {
      errors.push(`Maximum length is ${field.maxLength} characters`)
    }

    // Email validation (for type="email")
    if (field.type === 'email' && field.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(field.value)) {
        errors.push('Invalid email address')
      }
    }

    // Display errors or clear them
    if (errors.length > 0) {
      this.showFieldError(field, errors[0])
      return false
    } else {
      this.clearFieldError(field)
      return true
    }
  }

  showFieldError(field, message) {
    // Add Bootstrap invalid class
    field.classList.add('is-invalid')
    field.classList.remove('is-valid')

    // Find or create error message element
    let errorElement = field.parentElement.querySelector('.invalid-feedback')

    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.classList.add('invalid-feedback')
      field.parentElement.appendChild(errorElement)
    }

    errorElement.textContent = message
    errorElement.style.display = 'block'

    this.updateSubmitButton()
  }

  clearFieldError(field) {
    // Remove invalid class
    field.classList.remove('is-invalid')

    // Add valid class if field has content
    if (field.value.trim()) {
      field.classList.add('is-valid')
    } else {
      field.classList.remove('is-valid')
    }

    // Hide error message
    const errorElement = field.parentElement.querySelector('.invalid-feedback')
    if (errorElement) {
      errorElement.style.display = 'none'
    }

    this.updateSubmitButton()
  }

  updateSubmitButton() {
    if (!this.hasSubmitTarget) return

    // Check if any fields are invalid
    const hasInvalid = this.element.querySelector('.is-invalid')

    // Disable submit button if form is invalid
    this.submitTarget.disabled = !!hasInvalid
  }

  // Allow programmatic validation from other controllers
  isValid() {
    let valid = true
    this.fieldTargets.forEach(field => {
      if (!this.validateField(field)) {
        valid = false
      }
    })
    return valid
  }
}
